// web/src/routes/MatchDetail.tsx
// Route: /kids/:kidId/matches/:matchId — the COVER of this match's issue.
//
// Composition top→bottom:
//   - AchievementToastStack (from route state, weeks 1)
//   - Back link
//   - CoverHero (full-bleed): photo OR result swatch, masthead + sub-deck,
//     stickers (MoM, captain, milestones), sash (WIN/DRAW/LOSS/CUP), cover line
//   - Edit / Delete row
//   - Scoreline (animated count-up)
//   - PullQuote of the match note (if any)
//   - Photo manage strip (small — the hero IS the photo)
//   - Stats strip — 5 StatTiles with count-up
//   - Substitutions
//   - Timeline (icons)
//   - Voice transcript (if any)

import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMatchApi } from '../api/match';
import { usePlanLimitsApi } from '../api/stats';
import { ApiError } from '../api/client';
import { Button } from '../components/Button';
import { ShareMatchModal } from '../components/ShareMatchModal';
import { MatchPhoto } from '../components/MatchPhoto';
import { PitchHeatMap } from '../components/Pitch';
import { AchievementToastStack, type NewAchievement } from '../components/AchievementToast';
import { VoiceNoteSection } from '../components/VoiceNoteRecorder';
import { Bleed, Section } from '../components/Container';
import { CoverHero } from '../components/CoverHero';
import { CoverLineOverrideEditor } from '../components/CoverLineOverrideEditor';
import { Scoreline } from '../components/Scoreline';
import { PullQuote } from '../components/PullQuote';
import { StatTile } from '../components/StatTile';
import { Sticker } from '../components/Sticker';
import { Sash } from '../components/Sash';
import { Divider } from '../components/Divider';
import {
  IconGoal, IconAssist, IconShotOn, IconSave, IconDefensive,
  IconPitch, IconArrowLeft,
  EVENT_ICONS,
} from '../components/icons';
import { coverLineFor, subDeckFor, swatchColorVar } from '../utils/cover-line';
import type { Match, MatchEvent, MatchEventType, PlanLimits } from '../types';

const EVENT_LABEL: Record<MatchEventType, string> = {
  goal: 'Goal',
  assist: 'Assist',
  shot_on: 'Shot on target',
  shot_off: 'Shot off target',
  save: 'Save',
  defensive: 'Defensive action',
};

function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

function countEvents(m: Match, type: MatchEventType): number {
  return m.events.filter((e) => e.type === type).length;
}

function sortEvents(events: MatchEvent[]): MatchEvent[] {
  const halfRank = (h: MatchEvent['half']): number => (h === 1 ? 1 : h === 2 ? 2 : 3);
  return [...events].sort((a, b) => {
    const ra = halfRank(a.half), rb = halfRank(b.half);
    if (ra !== rb) return ra - rb;
    return a.minute - b.minute;
  });
}

function sashFor(match: Match): { label: string; tone: 'shout' | 'draw' | 'loss' | 'cup' | 'milestone' } | null {
  const goals = countEvents(match, 'goal');
  if (goals >= 3) return { label: 'HAT-TRICK!', tone: 'milestone' };
  if (match.matchType === 'cup') return { label: 'CUP TIE', tone: 'cup' };
  const us = match.ourScore, them = match.theirScore;
  if (typeof us !== 'number' || typeof them !== 'number') return null;
  if (us > them) {
    const diff = us - them;
    return { label: diff >= 4 ? 'ROUT!' : 'WIN!', tone: 'shout' };
  }
  if (us < them) return { label: 'LOSS', tone: 'loss' };
  return { label: 'DRAW', tone: 'draw' };
}

export function MatchDetail() {
  const { kidId, matchId } = useParams<{ kidId: string; matchId: string }>();
  const location = useLocation();
  const navState = location.state as { newAchievements?: NewAchievement[]; fromReveal?: boolean } | null;
  const [pendingAchievements] = useState<NewAchievement[]>(navState?.newAchievements ?? []);
  // When MatchDetail is reached via the post-save reveal screen, the score
  // moment has already happened — CoverHero should just appear, not
  // re-animate the cover line. Direct links / browser back / search land
  // with no fromReveal flag → entrance animation runs normally.
  const [arrivedFromReveal] = useState<boolean>(Boolean(navState?.fromReveal));
  const api = useMatchApi();
  const planLimitsApi = usePlanLimitsApi();
  const navigate = useNavigate();
  const [match, setMatch] = useState<Match | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [planLimits, setPlanLimits] = useState<PlanLimits | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // AI match report (Reports tier) — generation state. The persisted
  // `match.aiReport` is the source of truth on subsequent loads; these
  // states cover the in-flight UI.
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportCopied, setReportCopied] = useState(false);

  // Unified check across the legacy single field and the new array. Used to
  // gate the lazy photoUrl fetch and the gallery loader.
  const hasAnyPhoto = (m: Match): boolean =>
    (Array.isArray(m.imagePaths) && m.imagePaths.length > 0) || !!m.imagePath;

  const reload = async () => {
    if (!kidId || !matchId) return;
    try {
      const m = await api.get(kidId, matchId);
      setMatch(m);
      if (hasAnyPhoto(m)) {
        try { const r = await api.photoUrl(kidId, matchId); setPhotoUrl(r.url); } catch { setPhotoUrl(null); }
      } else { setPhotoUrl(null); }
    } catch (err) {
      const e = err as ApiError;
      if (e.status === 404) setNotFound(true);
      else setError(e.message || 'Could not load.');
    }
  };

  useEffect(() => {
    if (!kidId || !matchId) return;
    let cancelled = false;
    // Load plan-limits in parallel — needed for the photo gallery cap.
    planLimitsApi.get().then((pl) => { if (!cancelled) setPlanLimits(pl); }).catch(() => { /* fallback to free */ });
    (async () => {
      try {
        const m = await api.get(kidId, matchId);
        if (cancelled) return;
        setMatch(m);
        if (hasAnyPhoto(m)) {
          try {
            const r = await api.photoUrl(kidId, matchId);
            if (!cancelled) setPhotoUrl(r.url);
          } catch { /* fallback swatch */ }
        }
      } catch (err) {
        if (cancelled) return;
        const e = err as ApiError;
        if (e.status === 404) setNotFound(true);
        else setError(e.message || 'Could not load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kidId, matchId]);

  const onDelete = async () => {
    if (!kidId || !matchId) return;
    setDeleting(true);
    try {
      await api.remove(kidId, matchId);
      navigate(`/kids/${encodeURIComponent(kidId)}`);
    } catch (err) {
      const e = err as ApiError;
      setError(e.message || 'Could not delete.');
      setDeleting(false);
    }
  };

  /**
   * Generate (or regenerate) the AI match report. POST to /report; the
   * server runs Bedrock Sonnet and persists onto match.aiReport. We
   * splice the result into local state so the user sees it without a
   * full match reload.
   *
   * Failures map cleanly to user-facing strings:
   *   - 403 plan_required  → "Upgrade to Reports" — but the upsell card
   *                          is rendered before this point so we shouldn't
   *                          hit this path.
   *   - 503 generation_unavailable → "Couldn't generate just now…"
   *   - anything else      → server-supplied message or generic fallback.
   */
  const onGenerateReport = async () => {
    if (!kidId || !matchId || !match) return;
    setReportBusy(true);
    setReportError(null);
    setReportCopied(false);
    try {
      const { report } = await api.generateReport(kidId, matchId);
      setMatch({ ...match, aiReport: report });
    } catch (err) {
      const e = err as ApiError;
      setReportError(e.message || "Couldn't generate the report. Try again in a minute.");
    } finally {
      setReportBusy(false);
    }
  };

  const onCopyReport = async () => {
    if (!match?.aiReport?.text) return;
    try {
      await navigator.clipboard.writeText(match.aiReport.text);
      setReportCopied(true);
      window.setTimeout(() => setReportCopied(false), 2000);
    } catch {
      // Clipboard API can fail on insecure contexts / older browsers.
      // We surface a quiet error via the same banner so the user knows.
      setReportError('Copy failed. Select the text manually to copy.');
    }
  };

  if (loading) return <p style={{ color: 'var(--muted)' }}>Loading…</p>;

  if (notFound) {
    return (
      <div>
        <h1>Not found</h1>
        <p style={{ color: 'var(--muted)' }}>We couldn't find this match.</p>
        <Link to={`/kids/${encodeURIComponent(kidId || '')}`} style={{ color: 'var(--green)' }}>← Back</Link>
      </div>
    );
  }

  if (error || !match) {
    return (
      <div>
        <h1>Something went wrong</h1>
        <p style={{ color: 'var(--error)' }}>{error || 'Could not load.'}</p>
        <Link to={`/kids/${encodeURIComponent(kidId || '')}`} style={{ color: 'var(--green)' }}>← Back</Link>
      </div>
    );
  }

  const cover = coverLineFor(match);
  const sub = subDeckFor(match);
  const sash = sashFor(match);
  const sorted = sortEvents(match.events);
  const firstSecondHalfIdx = sorted.findIndex((e) => e.half === 2);
  const firstEtIdx = sorted.findIndex((e) => e.half === 'et');

  // Stickers — MoM, captain, milestones
  const stickers: React.ReactNode[] = [];
  if (match.manOfMatch) stickers.push(<Sticker key="mom" label="⭐ MoM" tone="mom" subLabel="STAR" />);
  if (match.captain) stickers.push(<Sticker key="cap" label="🏆 C" tone="captain" tilt={6} />);
  const goals = countEvents(match, 'goal');
  if (goals >= 3) stickers.push(<Sticker key="trick" label="🔥 HAT-TRICK" tone="milestone" tilt={-3} />);

  const issue = `${match.matchType.toUpperCase()}${match.matchFormat ? ` · ${match.matchFormat}` : ''} · ${formatDate(match.date).toUpperCase()}`;
  const subtitle = match.positions && match.positions.length
    ? match.positions.join(' · ')
    : match.position || undefined;

  return (
    <div>
      <AchievementToastStack achievements={pendingAchievements} />

      {/* Back link */}
      <Link
        to={`/kids/${encodeURIComponent(kidId || '')}`}
        style={{
          color: 'var(--muted)',
          fontSize: 'var(--small)',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          marginBottom: 'var(--space-base)',
        }}
      >
        <IconArrowLeft size={16} /> Back
      </Link>

      {/* ── Cover hero ────────────────────────────────────────────────── */}
      <Bleed>
        <CoverHero
          photoUrl={photoUrl}
          coverLine={cover.text}
          subtitle={[match.opposition ? `vs ${match.opposition}` : null, subtitle].filter(Boolean).join(' · ')}
          issue={issue}
          subDeck={sub}
          stickers={stickers}
          sash={sash ? <Sash label={sash.label} tone={sash.tone} corner="tr" /> : undefined}
          swatchVar={swatchColorVar(cover.swatch)}
          fallbackGlyph={typeof match.ourScore === 'number' ? match.ourScore : undefined}
          animateEntrance={!arrivedFromReveal}
        />
      </Bleed>

      {/* ── Cover-line manual override (2026-05-26) ──────────────────────
          Lets the parent rewrite the cover line if the AI got it wrong
          (or just didn't sing). Sits as a quiet eyebrow + pencil button
          below the hero — chic, doesn't compete with the cover itself. */}
      <CoverLineOverrideEditor
        kidId={kidId!}
        matchId={matchId!}
        currentText={cover.text}
        currentOverride={match.coverLineOverride}
        onSaved={(updated) => setMatch(updated)}
      />

      {/* Edit / Delete row */}
      <div style={{ marginTop: 'var(--space-xl)' }}>
        {confirmingDelete ? (
          <div
            style={{
              padding: 'var(--space-base)',
              background: 'var(--surface-1)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-base)',
            }}
          >
            <p style={{ margin: '0 0 12px' }}>Delete this match? You can't undo this from the app.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                label={deleting ? 'Deleting…' : 'Delete'}
                onClick={onDelete}
                loading={deleting}
                style={{ background: 'var(--electric-red)', color: 'var(--ink-on-green)' }}
              />
              <Button label="Cancel" variant="ghost" onClick={() => setConfirmingDelete(false)} />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--space-base)', flexWrap: 'wrap' }}>
            <Link
              to={`/kids/${encodeURIComponent(kidId!)}/matches/${encodeURIComponent(matchId!)}/edit`}
              style={{ textDecoration: 'none' }}
            >
              <Button label="Edit" variant="ghost" />
            </Link>
            <ShareButton
              kidId={kidId!}
              matchId={matchId!}
              cover={cover.text}
              opposition={match.opposition}
              ourScore={match.ourScore}
              theirScore={match.theirScore}
            />
            <Button
              label="Delete"
              variant="ghost"
              onClick={() => setConfirmingDelete(true)}
              style={{ color: 'var(--electric-red)' }}
            />
          </div>
        )}
      </div>

      {/* ── Scoreline ──────────────────────────────────────────────────── */}
      <Section spacing="lg">
        <Scoreline
          ourScore={match.ourScore}
          theirScore={match.theirScore}
          opposition={match.opposition}
          meta={typeof match.kidRating === 'number' ? `RATING · ${match.kidRating}/10` : undefined}
        />
      </Section>

      {/* ── Pull quote ─────────────────────────────────────────────────── */}
      {match.note ? (
        <Section spacing="lg">
          <PullQuote attribution="Match note">{match.note}</PullQuote>
        </Section>
      ) : null}

      {/* ── Photo manager (gallery — up to 1 free / 5 paid) ──────────── */}
      <Section spacing="lg">
        <Divider label={planLimits && planLimits.imagesPerMatchLimit > 1 ? 'Photos' : 'Photo'} />
        <MatchPhoto
          kidId={kidId!}
          matchId={matchId!}
          hasPhoto={hasAnyPhoto(match)}
          limit={planLimits?.imagesPerMatchLimit ?? 1}
          plan={planLimits?.plan}
          onChange={reload}
        />
      </Section>

      {/* ── Stats strip ───────────────────────────────────────────────── */}
      <Section spacing="lg">
        <Divider label="On the day" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12 }}>
          <StatTile value={countEvents(match, 'goal')} label="Goals" icon={<IconGoal size={14} />} accent="var(--accent-coral)" />
          <StatTile value={countEvents(match, 'assist')} label="Assists" icon={<IconAssist size={14} />} delay={0.08} />
          <StatTile value={countEvents(match, 'shot_on')} label="Shots on" icon={<IconShotOn size={14} />} delay={0.16} />
          <StatTile value={countEvents(match, 'save')} label="Saves" icon={<IconSave size={14} />} delay={0.24} />
          {typeof match.minutesPlayed === 'number' ? (
            <StatTile value={match.minutesPlayed} label="Minutes" icon={<IconPitch size={14} />} delay={0.32} />
          ) : null}
        </div>
      </Section>

      {/* ── Heat-map ──────────────────────────────────────────────────────
          Located events — coloured dots labelled by event type
          (Goal/Assist/Shot on/etc). Only renders when at least one event
          was logged with a pitch location.
          NOTE: the broadcast-style density blob (PitchDensityHeatMap)
          lives on KidDetail's season view, not here — with the 2-5
          events on a typical match it's just faint smudges and the
          dots tell the same story better (2026-05-19). */}
      {match.events.some((e) => typeof e.x === 'number' && typeof e.y === 'number') ? (
        <Section spacing="lg">
          <Divider label="Where it happened" />
          <PitchHeatMap events={match.events} />
        </Section>
      ) : null}

      {/* ── AI match report (Reports tier signature feature) ──────────────
          Three states on this section:
            - Reports + has report   → show the prose with Copy + Regenerate
            - Reports + no report    → "Generate AI match report" button
            - Stats / Free           → upsell strip pointing to /upgrade
          Backend gates at POST time so the button below never reaches a
          non-Reports parent; we still render the upsell so they SEE the
          feature exists. */}
      <Section spacing="lg">
        <Divider label="AI match report" />
        {planLimits?.plan === 'reports' ? (
          match.aiReport ? (
            <div
              style={{
                padding: 'var(--space-lg)',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <p
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  fontSize: 'var(--body)',
                  lineHeight: 1.6,
                  color: 'var(--ink)',
                }}
              >
                {match.aiReport.text}
              </p>
              <div style={{ marginTop: 'var(--space-base)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  label={reportCopied ? 'Copied ✓' : 'Copy'}
                  onClick={() => void onCopyReport()}
                />
                <Button
                  label={reportBusy ? 'Regenerating…' : 'Regenerate'}
                  variant="ghost"
                  onClick={() => void onGenerateReport()}
                  loading={reportBusy}
                />
                <span style={{ marginLeft: 'auto', fontSize: 'var(--small)', color: 'var(--muted)' }}>
                  Generated {new Date(match.aiReport.generatedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {reportError ? (
                <p style={{ color: 'var(--error)', marginTop: 'var(--space-sm)', fontSize: 'var(--small)' }}>{reportError}</p>
              ) : null}
            </div>
          ) : (
            <div
              style={{
                padding: 'var(--space-lg)',
                background: 'var(--surface)',
                border: '1px dashed var(--border)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center',
              }}
            >
              <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--small)', marginBottom: 'var(--space-base)' }}>
                A 2-paragraph write-up of this match — ready to paste into the team chat.
              </p>
              <Button
                label={reportBusy ? 'Writing…' : '✨ Generate report'}
                onClick={() => void onGenerateReport()}
                loading={reportBusy}
              />
              {reportError ? (
                <p style={{ color: 'var(--error)', marginTop: 'var(--space-sm)', fontSize: 'var(--small)' }}>{reportError}</p>
              ) : null}
            </div>
          )
        ) : (
          // Free + Stats — upsell. Reports owner gets the AI; co-parents
          // of a Reports household also get it (planLimits resolves to the
          // owner's plan in the backend).
          <div
            style={{
              padding: 'var(--space-lg)',
              background: 'linear-gradient(180deg, var(--surface-3), var(--green-dark))',
              color: 'var(--ink-on-green)',
              borderRadius: 'var(--radius-lg)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--font-masthead)',
                fontStyle: 'italic',
                fontSize: '1.25rem',
                textTransform: 'uppercase',
                letterSpacing: '-0.5px',
                marginBottom: 6,
              }}
            >
              Write-ups, on tap
            </div>
            <p style={{ margin: 0, fontSize: 'var(--small)', opacity: 0.85 }}>
              On the Reports plan, every match gets a 2-paragraph AI write-up
              — paste-ready for the team chat.{' '}
              <Link to="/upgrade" style={{ color: 'var(--gold-light)' }}>
                See Reports →
              </Link>
            </p>
          </div>
        )}
      </Section>

      {/* ── Substitutions ─────────────────────────────────────────────── */}
      {match.substitutions && match.substitutions.length > 0 ? (
        <Section spacing="lg">
          <Divider label="Substitutions" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[...match.substitutions].sort((a, b) => a.minute - b.minute).map((s, idx) => (
              <span
                key={idx}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-full)',
                  background: s.direction === 'on' ? 'rgba(42,104,69,0.12)' : 'rgba(107,98,88,0.08)',
                  color: s.direction === 'on' ? 'var(--accent-pitch)' : 'var(--muted)',
                  fontSize: 'var(--small)',
                  fontWeight: 600,
                }}
              >
                <span className="mfj-tnum">{s.minute}′</span>
                <span style={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 'var(--micro)' }}>
                  {s.direction === 'on' ? 'On' : 'Off'}
                </span>
              </span>
            ))}
          </div>
        </Section>
      ) : null}

      {/* ── Timeline ──────────────────────────────────────────────────── */}
      {sorted.length > 0 ? (
        <Section spacing="lg">
          <Divider label="Timeline" />
          <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {sorted.map((e, idx) => {
              const Icon = EVENT_ICONS[e.type] ?? IconDefensive;
              return (
                <li key={idx}>
                  {idx === firstSecondHalfIdx ? <HalfMarker label="Half-time" /> : null}
                  {idx === firstEtIdx && firstEtIdx !== -1 ? <HalfMarker label="Extra time" /> : null}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-base)',
                      padding: '10px 0',
                      borderTop: idx > 0 ? '1px solid var(--divider)' : 'none',
                    }}
                  >
                    <span
                      className="mfj-tnum"
                      style={{
                        width: 44,
                        fontFamily: 'var(--font-masthead)',
                        fontStyle: 'italic',
                        fontSize: '1.25rem',
                        color: 'var(--gold)',
                      }}
                    >
                      {/* Events without a minute recorded come back as 0 from
                          the form (Number('') === 0). 0 as a literal kickoff
                          minute is rare enough that treating it as "unrecorded"
                          is the better default — show "—" instead of "0′". */}
                      {e.minute > 0 ? `${e.minute}′` : '—'}
                    </span>
                    <span style={{ color: 'var(--green)' }}>
                      <Icon size={20} />
                    </span>
                    <span style={{ fontSize: 'var(--body)' }}>{EVENT_LABEL[e.type]}</span>
                    {e.note ? (
                      <span style={{ marginLeft: 8, color: 'var(--muted)', fontStyle: 'italic', fontSize: 'var(--small)' }}>
                        — {e.note}
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        </Section>
      ) : null}

      {/* ── Voice note — record / transcribing / transcript ───────────── */}
      {kidId && matchId ? (
        <VoiceNoteSection
          match={match}
          kidId={kidId}
          matchId={matchId}
          plan={planLimits?.plan}
          onMatch={setMatch}
        />
      ) : null}
    </div>
  );
}

/**
 * Share button using the Web Share API where available, with a copy-to-
 * clipboard fallback. We share the cover line + scoreline + URL so a
 * paste-into-WhatsApp reads like a magazine pull-quote rather than a bare
 * link. Status flips to "Copied!" for 2s on fallback success.
 *
 * No new shared component yet — kept inline here so it can be promoted
 * after the OG card endpoint (ROADMAP #8) is built and other surfaces
 * want to share too.
 */
function ShareButton({
  kidId,
  matchId,
  cover,
  opposition,
  ourScore,
  theirScore,
}: {
  kidId: string;
  matchId: string;
  cover: string;
  opposition?: string;
  ourScore?: number;
  theirScore?: number;
}) {
  const api = useMatchApi();
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const composeText = (): string => {
    const score =
      typeof ourScore === 'number' && typeof theirScore === 'number'
        ? `${ourScore}–${theirScore}`
        : '';
    const versus = opposition ? `vs ${opposition}` : '';
    return [cover, [score, versus].filter(Boolean).join(' '), '— from MyFootballJournal'].filter(Boolean).join(' · ');
  };

  /** Resolve a share URL (cached) and open the modal. */
  const openShareModal = async () => {
    if (busy) return;
    setBusy(true);
    let url = shareUrl;
    if (!url) {
      try {
        const created = await api.createShare(kidId, matchId);
        url = created.shareUrl;
        setShareUrl(url);
      } catch {
        url = typeof window !== 'undefined' ? window.location.href : '';
      }
    }
    setBusy(false);
    setModalOpen(true);
  };

  /** "Open share sheet" inside the modal → native Web Share / clipboard fallback. */
  const openSystemShare = async () => {
    const url = shareUrl ?? (typeof window !== 'undefined' ? window.location.href : '');
    const text = composeText();
    const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
    if (nav.share) {
      try {
        await nav.share({ title: cover, text, url });
        setModalOpen(false);
        return;
      } catch {
        // User cancelled — keep modal open so they can choose another option.
      }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
    } catch {
      // Silent. The modal's "Copy match link" already covers this case.
    }
  };

  return (
    <>
      <Button
        label={busy ? 'Preparing…' : 'Share'}
        variant="ghost"
        onClick={openShareModal}
        disabled={busy}
      />
      {modalOpen && shareUrl ? (
        <ShareMatchModal
          kidId={kidId}
          matchId={matchId}
          shareUrl={shareUrl}
          onOpenSystemShare={openSystemShare}
          onClose={() => setModalOpen(false)}
        />
      ) : null}
    </>
  );
}

function HalfMarker({ label }: { label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-base)',
        padding: '12px 0',
      }}
    >
      <span style={{ flex: 1, height: 1, background: 'var(--divider)' }} aria-hidden />
      <span
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--micro)',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        — {label} —
      </span>
      <span style={{ flex: 1, height: 1, background: 'var(--divider)' }} aria-hidden />
    </div>
  );
}
